import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lightbulb, Loader2, Check, X, Sparkles } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";

interface CleanerSuggestion {
  jobId: number;
  cleanerId: number;
  cleanerName: string;
  reason: string;
}

interface Props {
  unassignedJobIds: number[];
  cleaners: { id: number; name: string }[];
  onAssign: (jobId: number, cleanerId: number) => Promise<void>;
  getApartmentName: (jobId: number) => string;
}

export default function LumiCleanerSuggest({ unassignedJobIds, cleaners, onAssign, getApartmentName }: Props) {
  const [suggestions, setSuggestions] = useState<CleanerSuggestion[]>([]);
  const [overrides, setOverrides] = useState<Record<number, number>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isApplying, setIsApplying] = useState<number | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const { toast } = useToast();

  if (unassignedJobIds.length === 0 || dismissed) return null;

  const fetchSuggestions = async () => {
    setIsLoading(true);
    try {
      const response = await fetch("/api/admin/lumi/suggest-cleaner", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ jobIds: unassignedJobIds.slice(0, 10) }),
      });
      if (!response.ok) throw new Error("Failed");
      const data = await response.json();
      setSuggestions(data.suggestions || []);
      setHasFetched(true);
    } catch {
      toast({ title: "Could not get AI suggestions", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (suggestion: CleanerSuggestion) => {
    const cleanerId = overrides[suggestion.jobId] || suggestion.cleanerId;
    setIsApplying(suggestion.jobId);
    try {
      await onAssign(suggestion.jobId, cleanerId);
      setSuggestions(prev => prev.filter(s => s.jobId !== suggestion.jobId));
      toast({ title: `Job assigned successfully` });
      queryClient.invalidateQueries({ queryKey: ["/api/cleaning-jobs"] });
    } catch {
      toast({ title: "Failed to assign", variant: "destructive" });
    } finally {
      setIsApplying(null);
    }
  };

  const handleApproveAll = async () => {
    for (const s of suggestions) {
      await handleApprove(s);
    }
  };

  if (!hasFetched) {
    return (
      <Card className="border-amber-200/60 dark:border-amber-800/30 bg-gradient-to-r from-amber-50/80 to-orange-50/40 dark:from-amber-950/20 dark:to-orange-950/10 shadow-sm" data-testid="lumi-suggest-prompt">
        <CardContent className="p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm shrink-0">
              <Lightbulb className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold">{unassignedJobIds.length} unassigned cleaning job{unassignedJobIds.length > 1 ? 's' : ''}</p>
              <p className="text-xs text-muted-foreground">Lumi can suggest the best cleaner for each job based on workload and experience</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setDismissed(true)} className="text-xs" data-testid="btn-dismiss-suggest">
              Dismiss
            </Button>
            <Button size="sm" onClick={fetchSuggestions} disabled={isLoading} className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white" data-testid="btn-get-suggestions">
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
              Get AI Suggestions
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (suggestions.length === 0) return null;

  return (
    <Card className="border-amber-200/60 dark:border-amber-800/30 bg-gradient-to-r from-amber-50/80 to-orange-50/40 dark:from-amber-950/20 dark:to-orange-950/10 shadow-sm" data-testid="lumi-suggest-panel">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
              <Lightbulb className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-sm font-semibold">Lumi's Suggestions</span>
            <span className="text-[10px] text-muted-foreground">{suggestions.length} job{suggestions.length > 1 ? 's' : ''}</span>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setDismissed(true)} className="h-7 text-xs" data-testid="btn-dismiss-suggestions">
              <X className="w-3 h-3 mr-1" />
              Dismiss
            </Button>
            {suggestions.length > 1 && (
              <Button size="sm" onClick={handleApproveAll} className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white" data-testid="btn-approve-all">
                <Check className="w-3 h-3 mr-1" />
                Approve All
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-2">
          {suggestions.map((s) => (
            <div key={s.jobId} className="flex items-center gap-3 p-3 rounded-lg bg-white/60 dark:bg-white/5 border border-white/40 dark:border-white/10" data-testid={`suggestion-${s.jobId}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{getApartmentName(s.jobId)}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{s.reason}</p>
              </div>
              <Select
                value={String(overrides[s.jobId] || s.cleanerId)}
                onValueChange={(v) => setOverrides(prev => ({ ...prev, [s.jobId]: Number(v) }))}
              >
                <SelectTrigger className="w-[140px] h-8 text-xs" data-testid={`select-cleaner-${s.jobId}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {cleaners.map(c => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                onClick={() => handleApprove(s)}
                disabled={isApplying === s.jobId}
                className="h-8 px-3 bg-green-600 hover:bg-green-700 text-white"
                data-testid={`btn-approve-${s.jobId}`}
              >
                {isApplying === s.jobId ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
